import React, { useMemo, ReactNode } from "react";
import {
	Box,
	DialogActions,
	CircularProgress,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import { PrimaryButton } from "../../../../../common/components/CustomButton";
import { FluidButton } from "../../../../../common/components/FluidButton";
import { If } from "../../../../../common/components/If";
import { FluidNumber } from "../../../../../common/components/FluidNumberField";
import FluidSelect from "../../../../../common/components/FluidSelect";
import InfoBlock from "../../../../../common/components/InfoBlock/InfoBlock";
import {
	AveragingType,
	ContractPeriodType,
	FixingMethod,
	SettlementType,
} from "../../../../../requests_cm/gecoContractsService/types";
import { rollConventionOptions } from "../../../../../common/constants/invoicing";
import { PriceIndex } from "../../../../../requests_cm/gecoReferentialService/types";
import {
	indexSettlementValidator,
	fixPriceSettlementValidator,
} from "../../../formik/editSettlementValidationValidator";
import { borderNeutralSecondary } from "../../../../../core/theme";
import { Spacer } from "../../../../../common/components/Spacer";
import { FluidTextField } from "../../../../../common/components/FluidTextField";
import { FeesComponent } from "./FeesComponent";
import { NpqrsField } from "./NpqrsField";
import { getSyntheticEvent } from "../../../../../common/utils/getSyntheticEvent";
import { noop } from "../../../../../common/utils/operations";

export interface BaseEditableSettlementCard {
	settlement: SettlementType;
	contractPeriod: ContractPeriodType | undefined;
	isLoading: boolean;
	indexes: PriceIndex[];
	doesSupportNegativeCondition: boolean;
	validationSchema?: any | (() => any);
	onSubmit: (formikSettlement: SettlementType) => void;
	getAdditionalFields?: (props: {
		values: any;
		errors: any;
		setFieldValue: (name: string, value: any) => void;
		handleChange: (event: any) => void;
	}) => ReactNode;
}

const style = {
	card: {
		width: "680px",
		minHeight: "380px",
		padding: "16px 24px",
		border: `1px solid ${borderNeutralSecondary}`,
		backgroundColor: "white",
	},
	container: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		gap: "24px",
	},
	submitWrapper: {
		marginLeft: "auto",
	},
};


export function EditableSettlementCardBase({
	isLoading,
	settlement,
	contractPeriod,
	indexes,
	doesSupportNegativeCondition,
	onSubmit,
	getAdditionalFields = (_) => {
		return <React.Fragment></React.Fragment>;
	},
	validationSchema = settlement?.index
		? indexSettlementValidator
		: fixPriceSettlementValidator,
}:  Readonly<BaseEditableSettlementCard>) {
	const availableindexOptions = useMemo(() => {
		return (indexes || []).map((index: PriceIndex) => ({
			value: `${index.orchestrade_id}`,
			label: index.name,
		}));
	}, [indexes]);

	const rollConventionItems = useMemo(() => {
		const computationFrequency =
			contractPeriod?.invoicing?.monthly_settlement?.computation_frequency;
		if (!computationFrequency) {
			return [];
		}

		return rollConventionOptions[computationFrequency] || [];
	}, [contractPeriod, rollConventionOptions]);

	return (
		<Box sx={style.card}>
			<Formik
				validateOnChange={false}
				validateOnBlur={false}
				validationSchema={validationSchema}
				enableReinitialize
				initialValues={settlement}
				onSubmit={onSubmit}
			>
				{({
					handleChange,
					handleSubmit,
					setFieldValue,
					resetForm,
					values,
					errors,
					dirty,
				}) => (
					<form onSubmit={handleSubmit}>
						<Box sx={style.container}>
							<If condition={values.index}>
								<InfoBlock
									info={[
										{
											label: "Index",
											value:
												(
													availableindexOptions || []
												).find(
													(index) =>
														index.value ===
														`${values.index?.commodity_fixing_id}`
												)?.label || "",
										},
										{
											label: "Type",
											value: settlement?.type || "",
										},
										{
											label: "Period",
											value: settlement?.period || "",
										},
									]}
								/>
							</If>
							<If condition={values.price}>
								<InfoBlock
									info={[
										{
											label: "Type",
											value: settlement?.type || "",
										},
										{
											label: "Period",
											value: settlement?.period || "",
										},
									]}
								/>

							</If>
							<If condition={settlement?.period === "PERIODIC"}>

							<FluidSelect
								sx={{ width: "40%" }}
								isLabelStatic
								items={rollConventionItems}
								label={"Roll convention"}
								name={"roll_convention"}
								// @ts-ignore does not provides an HTMLElement as it should but the value itself
								onChange={(index: number) =>
									setFieldValue("roll_convention", index)
								}
								value={`${values?.roll_convention || ""}`}
								errorMessage={
									errors?.roll_convention
								}
							/>
							</If>
							<If condition={values.index}>
								<FluidSelect
									sx={{ width: "60%" }}
									isLabelStatic
									items={availableindexOptions}
									label={"Index"}
									name={"index.commodity_fixing_id"}
									// @ts-ignore does not provides an HTMLElement as it should but the value itself
									onChange={(index: number) => {
										setFieldValue(
											"index.commodity_fixing_id",
											index
										);
										setFieldValue(
											"index.name",
											(availableindexOptions || []).find(
												(indexOptions) =>
													indexOptions.value ===
													`${index}`
											)?.label || ""
										);
									}}
									value={`${values.index?.commodity_fixing_id}`}
									errorMessage={
										//@ts-ignore
										errors.index?.commodity_fixing_id
									}
								/>

								<FluidSelect
									sx={{ width: "60%" }}
									isLabelStatic
									items={[
										{
											value: FixingMethod.AVERAGING,
											label: "Averaging",
										},
										{
											value: FixingMethod.AVERAGING_NPQ,
											label: "Averaging NPQ",
										},
										{
											value: FixingMethod.AVERAGING_YEAR_TO_DATE,
											label: "Averaging Year to Date",
										},
									]}
									label={"Fixing Method"}
									name={"index.fixing_method"}
									value={`${values.index?.fixing_method}`}
									//@ts-ignore
									errorMessage={errors.index?.fixing_method}
									// @ts-ignore does not provides an HTMLElement as it should but the value itself
									onChange={(value: string) =>
										setFieldValue(
											"index.fixing_method",
											value
										)
									}
								/>
								<FluidSelect
									sx={{ width: "60%" }}
									isLabelStatic
									items={[
										{
											value: AveragingType.INDEX,
											label: "Monthly Average Price (Index)",
										},
										{
											value: AveragingType.DAILYWEIGHTED,
											label: "Daily Average Price (Daily Weighted)",
										},
										{
											value: AveragingType.WEIGHTED,
											label: "Hourly Average Price (Weighted)",
										},
									]}
									label={"Averaging Type"}
									name={"index.averaging_type"}
									value={`${values.index?.averaging_type}`}
									//@ts-ignore
									errorMessage={errors.index?.averaging_type}
									// @ts-ignore does not provides an HTMLElement as it should but the value itself
									onChange={(value: string) =>
										setFieldValue(
											"index.averaging_type",
											value
										)
									}
								/>
								<If
									condition={
										values.index?.fixing_method ===
										FixingMethod.AVERAGING_NPQ
									}
								>
									<NpqrsField
										settlementValues={values}
										//@ts-ignore
										errorMessage={errors?.index?.npqrs}
										name={"index.npqrs"}
										setFieldValue={setFieldValue}
									/>
									<FluidTextField
										value={values?.index?.ref_contract}
										label="Ref Contract"
										name={"index.ref_contract"}
										errorMessage={
											//@ts-ignore
											errors?.index?.ref_contract
										}
										onChange={handleChange}
									/>
								</If>
								<FluidNumber
									sx={{ width: "60%" }}
									onChange={handleChange}
									value={values?.index?.alpha?.commodity || 0}
									name={"index.alpha.commodity"}
									title={"Alpha"}
									errorMessage={
										//@ts-ignore
										errors?.index?.alpha?.commodity
									}
								/>

								<FeesComponent
									title={"Alpha Fees"}
									values={values}
									errors={errors}
									feeFieldName={"index.alpha.fees"}
									setFieldValue={setFieldValue}
									handleChange={handleChange}
								/>
								<FluidNumber
									sx={{ width: "60%" }}
									onChange={handleChange}
									value={values?.index?.beta?.commodity || 0}
									name={`index.beta.commodity`}
									title={"Beta"}
									errorMessage={
										//@ts-ignore
										errors?.index?.beta?.commodity
									}
								/>
								<FeesComponent
									title={"Beta Fees"}
									values={values}
									errors={errors}
									feeFieldName={"index.beta.fees"}
									setFieldValue={setFieldValue}
									handleChange={handleChange}
								/>
							</If>
							<If condition={values?.price}>
								<FluidNumber
									sx={{ width: "60%" }}
									onChange={handleChange}
									value={values?.price?.commodity || 0}
									name={`price.commodity`}
									title={"Fixed Price"}
									//@ts-ignore
									errorMessage={errors?.price?.commodity}
								/>
								<FeesComponent
									title={"Fees"}
									values={values}
									errors={errors}
									feeFieldName={"price.fees"}
									setFieldValue={setFieldValue}
									handleChange={handleChange}
								/>
							</If>
							{getAdditionalFields({
								values,
								errors,
								setFieldValue,
								handleChange,
							})}
							<If condition={doesSupportNegativeCondition}>
								<FormControlLabel
									name="condition_neg_price"
									control={
										<Checkbox
											data-testid="condition_neg_price_check"
											checked={values.condition_neg_price}
											onChange={(event) =>
												handleChange(
													getSyntheticEvent(
														"condition_neg_price",
														event.target.checked
													)
												)
											}
										/>
									}
									label="Settlement object is applicable to the negative price conditions indicated above"
								/>
							</If>
						</Box>

						<Spacer gap={24} />
						<DialogActions>
							<If condition={!isLoading && dirty}>
								<PrimaryButton
									onClick={() => resetForm()}
									text="Reset"
									type="button"
									color="secondary"
								/>
								<FluidButton
									type="submit"
									label={"Save"}
									onClick={noop}
								/>
							</If>
							<If condition={!!isLoading}>
								<CircularProgress />
							</If>
						</DialogActions>
					</form>
				)}
			</Formik>
		</Box>
	);
}
